import axios, { AxiosResponse } from "axios";
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseClient';
import {
  FolderData,
  ApiParams,
  ApiRequestError,
  ApiCallback,
  SSEMessage,
  MailCheckResponse,
  HashCheckResponse,
  RefreshScanResponse
} from './types';

// Cache for preventing duplicate requests
let prevParamsString = '';

/**
 * Fetches folder data from Supabase and transforms it into a hierarchical structure
 */
export const GetFolder = async (callback: (response: FolderData) => void): Promise<void> => {
  try {
    const snap = await getDocs(collection(db, 'folders'));
    const transformedData: FolderData = {};

    snap.docs.forEach(doc => {
      const { name } = doc.data() as { name: string };
      const [year, month, day] = name.split('.');
      if (!transformedData[year]) transformedData[year] = {};
      if (!transformedData[year][month]) transformedData[year][month] = [];
      if (!transformedData[year][month].includes(day)) transformedData[year][month].push(day);
    });

    for (const year in transformedData) {
      for (const month in transformedData[year]) {
        transformedData[year][month].sort((a, b) => parseInt(a) - parseInt(b));
      }
    }

    callback(transformedData);
  } catch (error) {
    throw new ApiRequestError('Failed to fetch folder data', 'FETCH_ERROR', error);
  }
};

/**
 * Generic data fetching with duplicate request prevention
 */
export const GetData = async <T = unknown>(url: string, params: ApiParams): Promise<T> => {
  const paramsString = JSON.stringify(params);

  if (prevParamsString === paramsString) {
    throw new ApiRequestError('Duplicate request detected', 'DUPLICATE_REQUEST');
  }

  prevParamsString = paramsString;

  try {
    const response: AxiosResponse<T> = await axios.get(url, { params });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ApiRequestError(
        error.message,
        error.code,
        error.response?.data
      );
    }
    throw error;
  }
};

/**
 * Fetches a song blob with callback pattern (for backward compatibility)
 * @deprecated Consider migrating to Promise-based pattern
 */
export const GetSong = async (
  url: string,
  callback: ApiCallback<Blob>
): Promise<void> => {
  try {
    const response: AxiosResponse<Blob> = await axios.get(url, {
      responseType: 'blob'
    });
    callback(response.data, null);
  } catch (error) {
    callback(null, error instanceof Error ? error : new Error(String(error)));
  }
};

/**
 * Server-Sent Events (SSE) client for real-time progress updates
 */
export const RefreshWithSSE = (
  url: string,
  onMessage: (data: SSEMessage) => void,
  onError: (error: Event | Error) => void,
  onComplete: () => void
): (() => void) => {
  const eventSource = new EventSource(url);

  eventSource.onmessage = (event: MessageEvent) => {
    try {
      const data: SSEMessage = JSON.parse(event.data);
      onMessage(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error parsing SSE data:', error);
      }
      onError(error instanceof Error ? error : new Error('Failed to parse SSE data'));
    }
  };

  eventSource.onerror = (error: Event) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('EventSource failed:', error);
    }
    onError(error);
    eventSource.close();
  };

  eventSource.addEventListener('end', () => {
    onComplete();
    eventSource.close();
  });

  // Return cleanup function
  return () => {
    eventSource.close();
  };
};

/**
 * 메일 ID로 업로드된 노래들을 조회합니다
 * @param mailId 메일의 고유 ID (Message-ID)
 * @returns 해당 메일에서 업로드된 노래 목록
 */
export const CheckMailUploaded = async (mailId: string): Promise<MailCheckResponse> => {
  try {
    const response: AxiosResponse<MailCheckResponse> = await axios.get(
      '/api/goodrecord/check-mail',
      { params: { mail_id: mailId } }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ApiRequestError(
        error.message,
        error.code,
        error.response?.data
      );
    }
    throw error;
  }
};

/**
 * 파일 해시로 중복 여부를 확인합니다
 * @param fileHash 파일의 MD5 해시값
 * @returns 중복 여부 및 기존 파일 정보
 */
export const CheckHashExists = async (fileHash: string): Promise<HashCheckResponse> => {
  try {
    const response: AxiosResponse<HashCheckResponse> = await axios.get(
      '/api/goodrecord/check-hash',
      { params: { hash: fileHash } }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ApiRequestError(
        error.message,
        error.code,
        error.response?.data
      );
    }
    throw error;
  }
};

export const ScanRefreshCandidates = async (): Promise<RefreshScanResponse> => {
  try {
    const response: AxiosResponse<RefreshScanResponse> = await axios.get('/api/goodrecord/scan');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ApiRequestError(
        error.message,
        error.code,
        error.response?.data
      );
    }
    throw error;
  }
};
